/** Bell с badge total counts (pending reviews + new orders + low stock).
 *
 * Click → dropdown с 3 секциями + ссылками на соответствующие страницы.
 */

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Bell, ShoppingBag, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface DashboardCounts {
  pending_orders: number;
  low_stock_products: number;
}

interface ReviewsCount {
  pagination: { total: number };
}

export function NotificationsBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: dashboard } = useQuery({
    queryKey: ["notif-dashboard"],
    queryFn: async () => {
      const { data } = await api.get(
        "/analytics/dashboard?period=last_30d",
      );
      return data as DashboardCounts;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: pendingReviews } = useQuery({
    queryKey: ["notif-reviews"],
    queryFn: async () => {
      const { data } = await api.get(
        "/reviews?status=pending&per_page=1",
      );
      return data as ReviewsCount;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const counts = {
    pendingOrders: dashboard?.pending_orders ?? 0,
    pendingReviews: pendingReviews?.pagination?.total ?? 0,
    lowStock: dashboard?.low_stock_products ?? 0,
  };
  const total =
    counts.pendingOrders + counts.pendingReviews + counts.lowStock;

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative"
      >
        <Bell className="w-5 h-5" />
        {total > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl border bg-card shadow-lg z-50">
          <div className="border-b px-4 py-3">
            <h3 className="font-heading font-semibold">Уведомления</h3>
          </div>

          <ul className="divide-y">
            <Row
              icon={<ShoppingBag className="w-4 h-4 text-info" />}
              label="Заказов ждут обработки"
              count={counts.pendingOrders}
              href="/orders?status=created"
              onClick={() => setOpen(false)}
            />
            <Row
              icon={<Star className="w-4 h-4 text-warning" />}
              label="Отзывов на модерации"
              count={counts.pendingReviews}
              href="/reviews?status=pending"
              onClick={() => setOpen(false)}
            />
            <Row
              icon={<AlertTriangle className="w-4 h-4 text-destructive" />}
              label="Товаров с низким стоком"
              count={counts.lowStock}
              href="/products?in_stock=false"
              onClick={() => setOpen(false)}
            />
          </ul>

          {total === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground text-center">
              Всё под контролем ✓
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  icon,
  label,
  count,
  href,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  href: string;
  onClick: () => void;
}) {
  return (
    <li>
      <Link
        to={href}
        onClick={onClick}
        className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50"
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="text-sm text-foreground">{label}</span>
        </div>
        <span
          className={`text-sm font-heading font-bold ${count > 0 ? "text-foreground" : "text-muted-foreground"}`}
        >
          {count}
        </span>
      </Link>
    </li>
  );
}
